import {
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  Button,
  Typography,
} from '@mui/material';
import {
  useImperativeHandle,
  forwardRef,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { ArrowUp, Mic, Network, Paperclip, Square, Trash2 } from 'lucide-react';
import styles from './InputBox.module.scss';
import type { AttachmentDraft } from '../../hooks';
import { useSpeechRecognition, usePromptHistory } from '../../hooks';
import AttachmentChipsRow from '../AttachmentChipsRow';
import DocumentPreviewDialog from 'components/DocumentPreviewDialog';
import apiReq from 'core/api';

interface InputBoxProps {
  onSendMessage: (message: string, obfuscation?: boolean) => void;
  loading: boolean;
  onClearChat?: () => void;
  onStopRequest?: () => void;
  hasActiveRequest: boolean;
  onMessageTextChange: (text: string) => void;
  initialValue?: string;
  attachmentDrafts?: AttachmentDraft[];
  onAttachFile?: (file: File) => void;
  onRemoveAttachment?: (localId: string) => void;
  onChangeAttachmentHeaders?: (localId: string, headers: string[] | undefined) => void;
  hasPendingAttachments?: boolean;
  hasReadyAttachments?: boolean;
  /** История промптов пользователя из текущего чата (от старых к свежим) для навигации ↑/↓. */
  messageHistory?: string[];
  mermaidActive?: boolean;
  onMermaidActivate?: () => void;
}

export interface InputBoxHandle {
  addText: (text: string) => void;
  clearText: () => void;
}

const InputBox = forwardRef<InputBoxHandle, InputBoxProps>(
  (
    {
      onSendMessage,
      loading,
      onClearChat,
      onStopRequest,
      hasActiveRequest,
      onMessageTextChange,
      initialValue = '',
      attachmentDrafts = [],
      onAttachFile,
      onRemoveAttachment,
      onChangeAttachmentHeaders,
      hasPendingAttachments = false,
      hasReadyAttachments = false,
      messageHistory = [],
      mermaidActive = false,
      onMermaidActivate,
    },
    ref
  ) => {
    const [messageText, setMessageText] = useState(initialValue);
    const [headersDraft, setHeadersDraft] = useState<AttachmentDraft | null>(null);
    // История отправленных промптов: навигация стрелками ↑/↓ как в терминале.
    const promptHistory = usePromptHistory(messageHistory);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const onMessageTextChangeRef = useRef(onMessageTextChange);
    const isInitializedRef = useRef(false);
    const prevInitialValueRef = useRef(initialValue);
    const [modelName, setModelName] = useState<string>('');
    // const [obfuscation, setObfuscation] = useState(() => {
    //   try {
    //     return localStorage.getItem('chat_obfuscation') === 'true';
    //   } catch {
    //     return false;
    //   }
    // });

    useEffect(() => {
      onMessageTextChangeRef.current = onMessageTextChange;
    }, [onMessageTextChange]);

    const getModelName = async () => {
      try {
        const res = await apiReq<{ model_name: string }>('llm/info/model');
        res?.model_name && setModelName(res.model_name);
      } catch {
        console.log('Не удалось получить версию ИИ-модели');
      }
    };

    useEffect(() => {
      getModelName();
    }, []);

    useEffect(() => {
      if (!isInitializedRef.current) {
        setMessageText(initialValue);
        if (onMessageTextChangeRef.current && initialValue) {
          onMessageTextChangeRef.current(initialValue);
        }
        isInitializedRef.current = true;
        prevInitialValueRef.current = initialValue;
      } else if (prevInitialValueRef.current !== initialValue) {
        if (initialValue === '') {
          setMessageText('');
        } else if (messageText === '' && initialValue !== '') {
          setMessageText(initialValue);
          if (onMessageTextChangeRef.current) {
            onMessageTextChangeRef.current(initialValue);
          }
        }
        prevInitialValueRef.current = initialValue;
      }
    }, [initialValue, messageText]);

    useImperativeHandle(ref, () => ({
      addText: (text: string) => {
        setMessageText((prev) => {
          const newText = prev ? prev + ' ' + text : text;
          if (onMessageTextChangeRef.current) {
            onMessageTextChangeRef.current(newText);
          }
          return newText;
        });
      },
      clearText: () => {
        setMessageText('');
        if (onMessageTextChangeRef.current) {
          onMessageTextChangeRef.current('');
        }
      },
    }));

    useEffect(() => {
      try {
        if (messageText) {
          localStorage.setItem('chat_message_text', messageText);
        } else {
          localStorage.removeItem('chat_message_text');
        }
      } catch (error) {
        console.warn('Failed to save message text to localStorage:', error);
      }

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (onMessageTextChangeRef.current) {
          if (window.requestIdleCallback) {
            window.requestIdleCallback(() => {
              if (onMessageTextChangeRef.current) {
                onMessageTextChangeRef.current(messageText);
              }
            });
          } else {
            if (onMessageTextChangeRef.current) {
              onMessageTextChangeRef.current(messageText);
            }
          }
        }
      }, 1000);

      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        try {
          if (messageText) {
            localStorage.setItem('chat_message_text', messageText);
          } else {
            localStorage.removeItem('chat_message_text');
          }
        } catch (error) {
          console.warn('Failed to save message text to localStorage on unmount:', error);
        }
      };
    }, [messageText]);

    // --- Голосовой ввод (Web Speech API) ---
    // Базовый текст на момент старта записи и накопленный финальный фрагмент.
    const recordingBaseRef = useRef('');
    const finalChunkRef = useRef('');

    const joinParts = (...parts: string[]) => parts.filter(Boolean).join(' ').replace(/\s+/g, ' ');

    const applyTranscript = useCallback((interim: string) => {
      const composed = joinParts(
        recordingBaseRef.current,
        finalChunkRef.current,
        interim
      ).trimStart();
      setMessageText(composed);
    }, []);

    const {
      isSupported: isSpeechSupported,
      isRecording,
      start,
      stop,
    } = useSpeechRecognition({
      onFinal: (transcript) => {
        finalChunkRef.current = joinParts(finalChunkRef.current, transcript);
        applyTranscript('');
      },
      onInterim: (transcript) => {
        applyTranscript(transcript);
      },
      onError: (error) => {
        if (error !== 'no-speech' && error !== 'aborted') {
          console.warn('Ошибка голосового ввода:', error);
        }
      },
    });

    const startRecording = useCallback(() => {
      if (isRecording) return;
      recordingBaseRef.current = messageText;
      finalChunkRef.current = '';
      start();
    }, [isRecording, start, messageText]);

    const handleMicClick = useCallback(() => {
      if (isRecording) {
        stop();
        return;
      }
      startRecording();
    }, [isRecording, stop, startRecording]);

    // Финальный текст сохраняем в localStorage/родителя после остановки записи.
    useEffect(() => {
      if (!isRecording && onMessageTextChangeRef.current) {
        onMessageTextChangeRef.current(messageText);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecording]);

    // Push-to-talk: удержание Пробела активирует запись, отпускание — останавливает.
    // Работает только когда фокус вне текстовых полей, чтобы не мешать набору пробелов.
    const startRecordingRef = useRef(startRecording);
    const stopRef = useRef(stop);
    useEffect(() => {
      startRecordingRef.current = startRecording;
      stopRef.current = stop;
    }, [startRecording, stop]);

    useEffect(() => {
      if (!isSpeechSupported) return;
      const spaceHeldRef = { current: false };

      const isEditableTarget = (target: EventTarget | null) => {
        const el = target as HTMLElement | null;
        if (!el) return false;
        const tag = el.tagName;
        return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.code !== 'Space' || e.repeat) return;
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        spaceHeldRef.current = true;
        startRecordingRef.current();
      };

      const handleKeyUp = (e: KeyboardEvent) => {
        if (e.code !== 'Space' || !spaceHeldRef.current) return;
        e.preventDefault();
        spaceHeldRef.current = false;
        stopRef.current();
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
      };
    }, [isSpeechSupported]);

    // const handleObfuscationToggle = useCallback(() => {
    //   setObfuscation((prev) => {
    //     const next = !prev;
    //     try {
    //       localStorage.setItem('chat_obfuscation', String(next));
    //     } catch (error) {
    //       console.warn('Failed to save obfuscation state to localStorage:', error);
    //     }
    //     return next;
    //   });
    // }, []);

    const handleSendClick = useCallback(() => {
      if (loading) return;
      if (hasPendingAttachments) return;
      const trimmed = messageText.trim();
      if (!trimmed && !hasReadyAttachments) return;
      onSendMessage(messageText, false);
      promptHistory.reset();
      setMessageText('');
      if (onMessageTextChangeRef.current) {
        onMessageTextChangeRef.current('');
      }
    }, [
      messageText,
      loading,
      onSendMessage,
      hasPendingAttachments,
      hasReadyAttachments,
      promptHistory,
    ]);

    const handlePaperclipClick = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const handleFilesPicked = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || !onAttachFile) return;
        Array.from(files).forEach((file) => onAttachFile(file));
        e.target.value = '';
      },
      [onAttachFile]
    );

    const isMac = useMemo(
      () => /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || navigator.userAgent.includes('Mac'),
      []
    );

    // Применить текст из истории: подставить и поставить курсор в конец.
    const applyHistoryText = useCallback((text: string) => {
      setMessageText(text);
      if (onMessageTextChangeRef.current) {
        onMessageTextChangeRef.current(text);
      }
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          const end = text.length;
          textarea.selectionStart = textarea.selectionEnd = end;
        }
      });
    }, []);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newText = e.target.value;
        setMessageText(newText);
        // Ручной ввод выводит из режима навигации по истории.
        promptHistory.reset();
        try {
          if (newText) {
            localStorage.setItem('chat_message_text', newText);
          } else {
            localStorage.removeItem('chat_message_text');
          }
        } catch (error) {
          console.warn('Failed to save message text to localStorage:', error);
        }
      },
      [promptHistory]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Навигация по истории промптов как в терминале.
        // ↑ — к старым (только если курсор в первой строке),
        // ↓ — к новым (только если курсор в последней строке).
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
          const textarea = textareaRef.current;
          if (!textarea) return;
          const { selectionStart, selectionEnd, value } = textarea;
          // Реагируем только когда нет выделения (как навигация курсором).
          if (selectionStart !== selectionEnd) return;

          if (e.key === 'ArrowUp') {
            const caretOnFirstLine = value.lastIndexOf('\n', selectionStart - 1) === -1;
            if (!caretOnFirstLine) return;
            const text = promptHistory.prev(value);
            if (text !== null) {
              e.preventDefault();
              applyHistoryText(text);
            }
          } else {
            const caretOnLastLine = value.indexOf('\n', selectionEnd) === -1;
            if (!caretOnLastLine) return;
            const text = promptHistory.next();
            if (text !== null) {
              e.preventDefault();
              applyHistoryText(text);
            }
          }
          return;
        }

        if (e.key === 'Enter') {
          const hasModifier = e.shiftKey || (isMac ? e.metaKey : e.ctrlKey);

          if (!hasModifier) {
            e.preventDefault();
            e.stopPropagation();
            handleSendClick();
          } else {
            e.preventDefault();
            const textarea = textareaRef.current;
            if (textarea) {
              const start = textarea.selectionStart;
              const end = textarea.selectionEnd;
              setMessageText((prevText) => {
                const newText = prevText.substring(0, start) + '\n' + prevText.substring(end);
                setTimeout(() => {
                  textarea.selectionStart = textarea.selectionEnd = start + 1;
                  textarea.focus();
                }, 0);
                return newText;
              });
            }
          }
        }
      },
      [isMac, handleSendClick, promptHistory, applyHistoryText]
    );

    const sendButtonClass =
      loading && hasActiveRequest && onStopRequest
        ? styles.sendButtonStop
        : messageText.trim() && !loading
          ? styles.sendButtonPrimary
          : styles.sendButtonDefault;

    return (
      <div className={styles.root}>
        <div className={`${styles.inputContainer}`}>
          <AttachmentChipsRow
            drafts={attachmentDrafts}
            onRemove={(localId) => onRemoveAttachment?.(localId)}
            onEditHeaders={onChangeAttachmentHeaders ? (d) => setHeadersDraft(d) : undefined}
          />
          <DocumentPreviewDialog
            open={!!headersDraft}
            documentId={headersDraft?.documentId ?? null}
            documentName={headersDraft?.name}
            selectedHeaders={headersDraft?.selectedHeaders}
            onApply={(headers) =>
              headersDraft && onChangeAttachmentHeaders?.(headersDraft.localId, headers)
            }
            onClose={() => setHeadersDraft(null)}
          />
          <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesPicked} />
          <div className={styles.inputWrap}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={10}
              placeholder="Введите сообщение..."
              variant="outlined"
              value={messageText}
              onChange={handleChange}
              inputRef={textareaRef}
              onKeyDown={handleKeyDown}
              InputProps={{
                className: styles.inputRoot,
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
                '& .MuiOutlinedInput-root': { border: 'none', boxShadow: 'none !important' },
                '& fieldset': { border: 'none' },
              }}
            />
          </div>
          <div className={styles.footerRow}>
            <div className={styles.footerLeft}>
              {onAttachFile && (
                <Tooltip title="Прикрепить файл">
                  <IconButton
                    size="small"
                    onClick={handlePaperclipClick}
                    className={styles.attachButton}
                    aria-label="Прикрепить файл"
                  >
                    <Paperclip size={16} strokeWidth={2} />
                  </IconButton>
                </Tooltip>
              )}
              {isSpeechSupported && (
                <Tooltip
                  title={
                    isRecording
                      ? 'Остановить запись'
                      : 'Голосовой ввод — или зажмите Пробел (вне поля ввода)'
                  }
                >
                  <IconButton
                    size="small"
                    onClick={handleMicClick}
                    className={`${styles.attachButton} ${isRecording ? styles.micButtonActive : ''}`}
                    aria-label={isRecording ? 'Остановить запись' : 'Голосовой ввод'}
                    aria-pressed={isRecording}
                  >
                    <Mic size={16} strokeWidth={2} />
                  </IconButton>
                </Tooltip>
              )}
              {onMermaidActivate && (
                <Tooltip
                  title={
                    mermaidActive
                      ? 'Деактивировать режим диаграмм Mermaid'
                      : 'Активировать режим диаграмм Mermaid'
                  }
                >
                  <IconButton
                    size="small"
                    onClick={onMermaidActivate}
                    className={`${styles.attachButton} ${mermaidActive ? styles.mermaidButtonActive : ''}`}
                    aria-label="Режим диаграмм Mermaid"
                    aria-pressed={mermaidActive}
                  >
                    <Network size={16} strokeWidth={2} />
                  </IconButton>
                </Tooltip>
              )}
              {/* <Tooltip
                title={
                  obfuscation
                    ? 'Данные будут обфусцированы перед отправкой в модель'
                    : 'Включите, чтобы скрыть персональные и конфиденциальные данные'
                }
              >
                <button
                  type="button"
                  className={`${styles.obfuscationToggle} ${
                    obfuscation ? styles.obfuscationToggleActive : ''
                  }`}
                  onClick={handleObfuscationToggle}
                  aria-pressed={obfuscation}
                  aria-label="Обфусцировать данные"
                >
                  <Shield size={14} strokeWidth={2.25} />
                  <span>Обфусцировать данные</span>
                </button>
              </Tooltip> */}
              <Typography variant="caption" className={styles.modelName}>
                {modelName ? modelName.toUpperCase() : ''}
              </Typography>
            </div>
            <div className={styles.footerRight}>
              {onClearChat && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Trash2 size={14} strokeWidth={2} />}
                  onClick={onClearChat}
                  className={styles.clearButton}
                >
                  Очистить чат
                </Button>
              )}
              <Typography variant="caption" className={styles.hintRight}>
                <span className={styles.kbd}>
                  {isMac ? '⌘' : 'Ctrl'}
                  <span className={styles.kbdSep}>+</span>
                  <span>↵</span>
                </span>
                <span className={styles.hintMid}>или</span>
                <span className={styles.kbd}>
                  Shift
                  <span className={styles.kbdSep}>+</span>
                  <span>↵</span>
                </span>
                <span className={styles.hintEnd}>для новой строки</span>
              </Typography>
            </div>
            <Tooltip
              title={loading && hasActiveRequest ? 'Отменить запрос' : 'Отправить сообщение'}
            >
              <div className={styles.sendWrap}>
                <IconButton
                  color="primary"
                  disableRipple
                  onClick={
                    loading && hasActiveRequest && onStopRequest ? onStopRequest : handleSendClick
                  }
                  disabled={
                    loading
                      ? false
                      : hasPendingAttachments || (!messageText.trim() && !hasReadyAttachments)
                  }
                  className={`${styles.sendButton} ${sendButtonClass}`}
                >
                  {!loading && (
                    <div className={styles.iconBox}>
                      <ArrowUp size={18} color="currentColor" strokeWidth={2.5} />
                    </div>
                  )}
                  {loading && hasActiveRequest && onStopRequest ? (
                    <div className={styles.iconBox}>
                      <Square
                        size={14}
                        color="currentColor"
                        strokeWidth={2.5}
                        fill="currentColor"
                      />
                    </div>
                  ) : loading ? (
                    <CircularProgress size={18} thickness={4} />
                  ) : null}
                </IconButton>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }
);

InputBox.displayName = 'InputBox';

export default InputBox;
